import React, { useEffect, useState } from 'react';
import { LuInfo } from 'react-icons/lu';

import { AccountGroup, AccountGroupAdvisorUpdateRequest, DataType } from '@ndvr/portfolio-api';

import { EditableCard } from '@/components/EditableCard';
import { useRequiredParams } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useUpdateAccountGroupAsAdvisor } from 'views/portfolio/account-group-queries';

export const GeneralCard = () => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: profile, isPending: isGroupProfileLoading } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    const accountGroup = profile?.accountGroup;

    const { mutate: updateAccountGroupAsAdvisor, isPending: isUpdateAccountGroupAsAdvisorPending } =
        useUpdateAccountGroupAsAdvisor(accountGroupGUID);

    const [editing, setEditing] = useState(false);
    const [editableAccountGroup, setEditableAccountGroup] = useState<Partial<AccountGroup> | undefined>(undefined);

    useEffect(() => {
        if (accountGroup) {
            setEditableAccountGroup(accountGroup);
        }
    }, [accountGroup]);

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancel = () => {
        setEditableAccountGroup(accountGroup);
        setEditing(false);
    };

    const handleSave = () => {
        const changedValues: Partial<AccountGroup> = {};

        if (editableAccountGroup?.accountGroupName?.plainText !== accountGroup?.accountGroupName.plainText) {
            changedValues.accountGroupName = editableAccountGroup?.accountGroupName;
        }

        updateAccountGroupAsAdvisor({ ...accountGroup, ...changedValues } as AccountGroupAdvisorUpdateRequest, {
            onSuccess: () => {
                setEditing(false);
            },
        });
    };

    const hasChanges = editableAccountGroup?.accountGroupName?.plainText !== accountGroup?.accountGroupName.plainText;

    return (
        <EditableCard
            title="General"
            icon={LuInfo}
            loading={isGroupProfileLoading}
            editing={editing}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            hasChanges={hasChanges}
            saveDisabled={!hasChanges}
            saving={isUpdateAccountGroupAsAdvisorPending}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-600">Name</div>
                    {editing ? (
                        <input
                            type="text"
                            value={editableAccountGroup?.accountGroupName?.plainText || ''}
                            onChange={(e) =>
                                setEditableAccountGroup((prev) => ({
                                    ...prev,
                                    accountGroupName: {
                                        plainText: e.target.value,
                                        encryptionContextGUID: accountGroup?.householdGUID || '',
                                        dataType: DataType.PORTFOLIO_NAME,
                                    },
                                }))
                            }
                            className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        />
                    ) : (
                        <div>{accountGroup?.accountGroupName.plainText}</div>
                    )}
                </div>
            </div>
        </EditableCard>
    );
};
